import {
  DocumentCheckOutlineIcon,
  DocumentOutlineIcon,
  ErrorFillIcon,
  ImageOutlineIcon,
  PlayArrowOutlineIcon,
  WarningFillIcon,
} from '@applyboard/ui-icons';
import React, { useState } from 'react';
import { IconType, IntentProps, useFullTheme } from '~/design';
import {
  useButtonBaseStyle,
  useButtonEmphasisStyle,
  useButtonSizeStyle,
} from '~/internal/InternalButton';
import { Text } from '../Text';
import { allowedTooltipSymbols } from '~/utils/tooltip';
import { Tooltip } from '../Tooltip';
import { svgVerticalAlign } from '~/utils/styling';
import { IconWrapper } from '~/internal';
import { ProgressIndicator } from '../ProgressIndicator';

export interface FileListContextProps {
  disabled?: boolean;
  handleItemRemoval: (index: number) => void;
  numItems: number;
  onDownload?: (id: string) => void;
  onRemove?: ((id: string) => void) | null;
  orientation?: 'horizontal' | 'vertical';
  size?: 'sm' | 'md';
}

export const FileListContext = React.createContext<FileListContextProps | null>(null);

export type FileType = 'image' | 'video' | 'document' | null;

export function getFileTypeFromName(name: string): FileType {
  const parts = name.split('.');
  const extension = parts[parts.length - 1];
  return ['png', 'jpeg', 'jpg', 'webp', 'gif'].includes(extension)
    ? 'image'
    : ['doc', 'docx', 'pdf', 'txt', 'odt', 'rtf', 'epub'].includes(extension)
      ? 'document'
      : ['mp4', 'mov', 'avi', 'wmv', 'm4v'].includes(extension)
        ? 'video'
        : null;
}

export function getFileTypeFromMime(mimeType: string): FileType {
  return mimeType.startsWith('image')
    ? 'image'
    : mimeType.startsWith('video')
      ? 'video'
      : mimeType === 'application/pdf'
        ? 'document'
        : null;
}

export interface InternalFileIconProps extends IntentProps {
  fileType: FileType;
}

export function InternalFileIcon({ fileType, intent }: InternalFileIconProps) {
  const Icon =
    fileType === 'image'
      ? ImageOutlineIcon
      : fileType === 'video'
        ? PlayArrowOutlineIcon
        : fileType === 'document'
          ? DocumentCheckOutlineIcon
          : DocumentOutlineIcon;
  return <Icon intent={intent} />;
}

export interface FileIconProps extends IntentProps {
  fileName: string;
}

export function FileIcon({ fileName, intent }: FileIconProps) {
  const fileType = getFileTypeFromName(fileName);
  return (
    <IconWrapper allowInteractions={false} intent={intent ?? 'secondary'} selector="div">
      <InternalFileIcon fileType={fileType} />
    </IconWrapper>
  );
}

export function FileIntentIcon({
  intent = 'secondary',
  isLast,
}: IntentProps & {
  isLast?: boolean;
}) {
  const context = React.useContext(FileListContext);
  if (!context) {
    throw new Error('Missing context');
  }

  const icon =
    intent === 'negative' ? (
      <ErrorFillIcon intent="negative" />
    ) : intent === 'warning' ? (
      <WarningFillIcon intent="warning" />
    ) : null;

  const { colors } = useFullTheme();
  const buttonSizeStyle = useButtonSizeStyle({ isIconOnly: true, size: context.size });
  const { borderRadiuses } = useFullTheme();

  if (!icon) {
    return null;
  }

  return (
    <div
      css={[
        buttonSizeStyle,
        {
          alignItems: 'center',
          backgroundColor: {
            magic: colors.bgMagicHighlight,
            negative: colors.bgNegativeHighlight,
            positive: colors.bgPositiveHighlight,
            primary: colors.bgPrimaryHighlight,
            secondary: colors.bgSecondaryHighlight,
            warning: colors.bgWarningHighlight,
          }[intent],
          display: 'flex',
          justifyContent: 'center',
        },
        isLast && {
          borderTopRightRadius: borderRadiuses.control,
          borderBottomRightRadius: borderRadiuses.control,
        },
      ]}
    >
      {icon}
    </div>
  );
}

interface FileNameButtonProps extends IntentProps {
  as?: 'button' | 'div' | 'a';
  emphasis?: 'highlighted' | 'outlined';
  fileType: FileType;
  hideIcon?: boolean;
  href?: string;
  name: string;
  // This is intentionally called `removeFile` instead of `onRemove` because for some reason the
  // prop was getting overwritten by radix-ui's react-slot utility. This has been fixed in this
  // PR: https://github.com/radix-ui/primitives/pull/1713 but I think we need to upgrade.
  removeFile?: FileListContextProps['onRemove'];
  uploading?: boolean;
}

const FileNameButton = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, FileNameButtonProps>(
  function FileNameButton(
    {
      as: Tag = 'button',
      emphasis = 'highlighted',
      fileType,
      hideIcon,
      intent = 'secondary',
      name,
      href,
      removeFile,
      uploading,
      ...props
    },
    ref,
  ) {
    const context = React.useContext(FileListContext);
    if (!context) {
      throw new Error('Missing context');
    }
    const { spacing } = useFullTheme();
    const buttonBaseStyle = useButtonBaseStyle({ allowInteractions: Tag === 'a', intent });
    const buttonEmphasisStyle = useButtonEmphasisStyle({
      allowInteractions: Tag === 'a',
      borderless: removeFile || href ? ['right'] : [],
      emphasis,
      intent,
      useEnabledPseudoClass: false,
    });
    const buttonSizeStyle = useButtonSizeStyle({ size: context.size });
    const hasIntentIcon = ['warning', 'negative'].includes(intent);
    const rounded = !href && !removeFile && !hasIntentIcon;

    const maxWidthOffset =
      (uploading ? 1 : [href, removeFile, hasIntentIcon].filter(Boolean).length) * 40;

    return (
      <Tag
        css={[
          buttonBaseStyle,
          buttonEmphasisStyle,
          buttonSizeStyle,
          {
            alignItems: 'center',
            display: 'flex',
            gap: spacing[1],
            maxWidth:
              context.orientation === 'vertical'
                ? `calc(100% - ${maxWidthOffset}px)`
                : 220 + (hideIcon ? 24 + 4 : 0),
            paddingLeft: spacing[4],
            paddingRight: spacing[4],
            textDecoration: 'none',
            width: context.orientation === 'vertical' ? '100%' : undefined,
            '&:hover': {
              boxShadow: 'none',
            },
          },
          !rounded && {
            borderBottomRightRadius: 0,
            borderTopRightRadius: 0,
          },
        ]}
        {...props}
        {...(href
          ? {
              href,
              rel: 'noopener noreferrer',
              target: '_blank',
            }
          : {})}
        // @ts-expect-error HTMLButtonElement ref isn't allowed for div elements but this conditional isn't passing type-checking
        ref={ref}
      >
        {!hideIcon && (
          <IconWrapper intent={intent} selector={Tag}>
            <InternalFileIcon fileType={fileType} />
          </IconWrapper>
        )}
        <Text as="span" intent={intent} variant="labelM" overflow="ellipsis">
          {name}
        </Text>
      </Tag>
    );
  },
);
// This allows FileNameButton to be wrapped in Tooltip
Object.assign(FileNameButton, { [allowedTooltipSymbols.FileListItem]: true });

export function FileNameButtonWrapper({
  emphasis,
  fileType,
  hideIcon,
  href,
  intent,
  name,
  removeFile,
  uploading,
}: FileNameButtonProps) {
  const context = React.useContext(FileListContext);
  if (!context) {
    throw new Error('Missing context');
  }
  const props = {
    emphasis,
    fileType,
    hideIcon,
    href,
    intent,
    name,
    removeFile,
    uploading,
  } as const;

  const [hasEllipsis, setHasEllipsis] = useState(false);
  const buttonRef = (fileNameButton: HTMLButtonElement | HTMLAnchorElement | null) => {
    if (fileNameButton) {
      const textLabel = Array.from(fileNameButton.childNodes).find(
        node => node.nodeName === 'SPAN',
      ) as HTMLElement;
      if (textLabel) {
        setHasEllipsis(textLabel.offsetWidth < textLabel.scrollWidth);
      }
    }
  };

  if (hasEllipsis) {
    return (
      <Tooltip label={name}>
        <FileNameButton ref={buttonRef} {...props} as={href ? 'a' : 'button'} />
      </Tooltip>
    );
  }

  return <FileNameButton ref={buttonRef} {...props} as={href ? 'a' : 'div'} />;
}

export function FileListIconButton({
  'aria-label': ariaLabel,
  disabled: _disabled = false,
  emphasis = 'highlighted',
  href,
  intent = 'secondary',
  icon: Icon,
  isLast = false,
  onClick,
}: {
  'aria-label': string;
  disabled?: boolean;
  emphasis?: 'highlighted' | 'outlined';
  href?: string;
  icon: IconType;
  isLast?: boolean;
  onClick: () => void;
} & IntentProps) {
  const context = React.useContext(FileListContext);
  if (!context) {
    throw new Error('Missing context');
  }
  const Tag = href ? 'a' : 'button';
  const buttonBaseStyle = useButtonBaseStyle({ intent });
  const buttonEmphasisStyle = useButtonEmphasisStyle({
    borderless: isLast ? ['left'] : ['left', 'right'],
    emphasis,
    intent,
    useEnabledPseudoClass: Tag === 'button',
  });
  const buttonSizeStyle = useButtonSizeStyle({ isIconOnly: true, size: context.size });
  const disabled = Tag === 'a' ? false : _disabled || context.disabled;
  const { colors } = useFullTheme();

  return (
    <Tag
      type={Tag === 'button' ? 'button' : undefined}
      aria-label={ariaLabel}
      css={[
        buttonBaseStyle,
        buttonEmphasisStyle,
        buttonSizeStyle,
        svgVerticalAlign,
        {
          alignItems: 'center',
          backgroundColor: {
            primary: colors.bgPrimaryHighlight,
            secondary: colors.bgSecondaryHighlight,
            positive: colors.bgPositiveHighlight,
            negative: colors.bgNegativeHighlight,
            warning: colors.bgWarningHighlight,
            magic: colors.bgMagicHighlight,
          }[intent],
          borderBottomLeftRadius: 0,
          borderTopLeftRadius: 0,
          boxShadow: 'none',
          display: 'flex',
          justifyContent: 'center',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        !isLast && {
          borderBottomRightRadius: 0,
          borderTopRightRadius: 0,
        },
      ]}
      data-btn-type={href ? undefined : 'remove'}
      disabled={Tag === 'a' ? undefined : disabled || context.disabled}
      onClick={onClick}
      {...(href
        ? {
            href,
            rel: 'noopener noreferrer',
            target: '_blank',
          }
        : {})}
    >
      <IconWrapper contrast={disabled ? 'low' : 'high'} intent={intent} selector={Tag}>
        <Icon />
      </IconWrapper>
    </Tag>
  );
}

export function UploadingIndicator({
  emphasis = 'highlighted',
  intent = 'secondary',
  uploading = false,
}: {
  emphasis?: 'highlighted' | 'outlined';
  uploading?: boolean;
} & IntentProps) {
  const context = React.useContext(FileListContext);
  if (!context) {
    throw new Error('Missing context');
  }
  const buttonBaseStyle = useButtonBaseStyle({ allowInteractions: false, intent });
  const buttonSizeStyle = useButtonSizeStyle({ isIconOnly: true, size: context.size });
  const buttonEmphasisStyle = useButtonEmphasisStyle({
    allowInteractions: false,
    borderless: ['left'],
    emphasis,
    intent,
    useEnabledPseudoClass: false,
  });

  if (!uploading) {
    return null;
  }

  return (
    <div
      css={[
        buttonBaseStyle,
        buttonSizeStyle,
        buttonEmphasisStyle,
        {
          alignItems: 'center',
          borderBottomLeftRadius: 0,
          borderTopLeftRadius: 0,
          display: 'flex',
          justifyContent: 'center',
        },
      ]}
    >
      <ProgressIndicator aria-label="Uploading file" variant="circular" indeterminate />
    </div>
  );
}
